export default class VideoRecorder {
  constructor(videoElement) {
    this.videoElement = videoElement
    this.mediaRecorder = null
    this.recordedChunks = []
    this.isRecording = false
    this.recordingTimeout = null
    this.audioContext = null
    this.sourceNode = null
    this.destinationNode = null
    this.gainNode = null
  }

  setMuted(muted) {
    // If AudioContext is active, control mute via gain node
    if (this.gainNode) {
      this.gainNode.gain.value = muted ? 0 : 1
      return true
    }
    return false
  }

  cleanup() {
    // Cleanup audio context when video player is destroyed
    if (this.sourceNode) {
      this.sourceNode.disconnect()
      this.sourceNode = null
    }
    if (this.gainNode) {
      this.gainNode.disconnect()
      this.gainNode = null
    }
    if (this.destinationNode) {
      this.destinationNode = null
    }
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
  }

  async startRecording() {
    try {
      if (this.videoElement.readyState < 2) {
        throw new Error('Video not ready. Please wait for video to load.')
      }

      this.recordedChunks = []

      // Get the video stream
      const stream =
        this.videoElement.captureStream?.() || this.videoElement.mozCaptureStreamUntilEnded?.()

      const videoTrack = stream.getVideoTracks()[0]

      // Create a new MediaStream with just the video track
      const newStream = new MediaStream([videoTrack])

      // Firefox fix: Use Web Audio API to route audio for both playback and recording
      const audioTrack = stream.getAudioTracks()[0]
      if (audioTrack) {
        try {
          // Create AudioContext for audio routing
          this.audioContext = new (window.AudioContext || window.webkitAudioContext)()

          // Create source from video element
          this.sourceNode = this.audioContext.createMediaElementSource(this.videoElement)

          // Create gain node for volume/mute control
          this.gainNode = this.audioContext.createGain()

          // Create destination for recording
          this.destinationNode = this.audioContext.createMediaStreamDestination()

          // Connect: source -> gain -> speakers AND recording
          this.sourceNode.connect(this.gainNode)
          this.gainNode.connect(this.audioContext.destination) // To speakers
          this.gainNode.connect(this.destinationNode) // To recorder

          // Use the audio from destination for recording
          const recordingAudioTrack = this.destinationNode.stream.getAudioTracks()[0]
          if (recordingAudioTrack) {
            newStream.addTrack(recordingAudioTrack)
          }
        } catch (audioError) {
          console.warn('Failed to setup audio routing, recording without audio:', audioError)
          // Fallback: just add the original audio track
          newStream.addTrack(audioTrack)
        }
      }

      const mimeTypes = [
        'video/webm;codecs=vp9,opus', // VP9 video + Opus audio
        'video/webm;codecs=vp8,opus', // VP8 video + Opus audio
        'video/webm;codecs=h264,opus', // H264 video + Opus audio
        'video/webm', // Generic webm
        'video/mp4', // Fallback MP4
      ]

      let selectedMimeType
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType
          break
        }
      }

      if (!selectedMimeType) {
        throw new Error('No supported MIME type found for recording')
      }

      // Create MediaRecorder
      this.mediaRecorder = new MediaRecorder(newStream, {
        mimeType: selectedMimeType,
        videoBitsPerSecond: 5000000,
      })

      // Set up data handling
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data)
          console.log('Chunk received:', event.data.size, 'bytes')
        }
      }

      // Start the recording
      this.mediaRecorder.start(100)
      this.isRecording = true
      console.log('Recording started with MIME type:', selectedMimeType)

      // Start a safety timeout to ensure we get data
      this.recordingTimeout = setTimeout(() => {
        if (this.recordedChunks.length === 0) {
          this.mediaRecorder.requestData()
        }
      }, 1000)

      // Start the video if it's not playing
      if (this.videoElement.paused) {
        await this.videoElement.play()
        this.videoElement.muted = false
      }
    } catch (error) {
      console.error('Error starting recording:', error)
      throw error
    }
  }

  stopRecording() {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        reject(new Error('No active recording'))
        return
      }

      // Clear the safety timeout
      if (this.recordingTimeout) {
        clearTimeout(this.recordingTimeout)
        this.recordingTimeout = null
      }

      this.mediaRecorder.onstop = () => {
        // Don't cleanup audio context - keep it alive so audio continues to work
        // Disconnect from recording destination but keep speaker connection via gain node
        if (this.gainNode && this.destinationNode) {
          // Disconnect gain from recording destination only
          try {
            this.gainNode.disconnect(this.destinationNode)
          } catch (e) {
            // Already disconnected, ignore
          }
          this.destinationNode = null
        }
        // Keep audioContext, sourceNode, and gainNode alive for future audio playback and mute control

        if (this.recordedChunks.length === 0) {
          // Request data one last time before declaring no data
          this.mediaRecorder.requestData()
          setTimeout(() => {
            if (this.recordedChunks.length === 0) {
              reject(new Error('No data was recorded'))
              return
            }
            this.finalizeRecording(resolve)
          }, 100)
        } else {
          this.finalizeRecording(resolve)
        }
      }

      this.mediaRecorder.stop()
    })
  }

  finalizeRecording(resolve) {
    const blob = new Blob(this.recordedChunks, {
      type: this.mediaRecorder.mimeType,
    })

    console.log('Created blob of size:', blob.size)
    this.isRecording = false
    resolve(blob)
  }

  download(filename = 'recorded-video.webm') {
    if (this.recordedChunks.length === 0) {
      throw new Error('No recorded data available')
    }

    const blob = new Blob(this.recordedChunks, {
      type: this.mediaRecorder.mimeType,
    })

    if (blob.size === 0) {
      throw new Error('Generated blob has zero size')
    }

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    document.body.appendChild(a)
    a.style.display = 'none'
    a.href = url
    a.download = filename
    a.click()

    setTimeout(() => {
      URL.revokeObjectURL(url)
      document.body.removeChild(a)
    }, 100)
  }
}
